import { useMemo } from "react";
import { STATUS, OPERATORS } from "@/lib/api";
import { PtBadge } from "@/components/PtBadge";

function currentOf(slots, port) {
  const list = slots.filter((s) => s.port === port);
  const cur = list.find((s) => s.current) || list.find((s) => ["active", "nobalance", "error", "inserted", "registering"].includes(s.status));
  return cur || null;
}

const SHORT = { active: ["ON", "text-green-600"], nobalance: ["ON", "text-green-600"], inserted: ["ADA SIM", "text-slate-600"], registering: ["MEMBACA", "text-green-600"], error: ["ERROR", "text-red-600"] };

function Cell({ port, slot, s, isCur, colors, q, onOpen }) {
  const st = STATUS[s?.status] || STATUS.empty;
  const dim = q && ![s?.name, s?.company, s?.number].some((v) => (v || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <button data-testid={`slot-tile-${port}-${slot}`} onClick={() => onOpen(port, slot)} title={`${port}.${slot}${s?.company ? ` · ${s.company}` : ""}`}
      className={`text-left rounded border px-1.5 py-1 min-w-0 transition-colors duration-150 hover:border-emerald-500 ${isCur ? "border-green-300 bg-green-50/60" : "border-slate-200 bg-white"} ${dim ? "opacity-25" : ""}`}>
      <div className="flex items-center gap-1 min-w-0">
        <span className={`text-[12px] font-semibold leading-tight truncate ${s?.name ? (isCur ? "text-emerald-700" : "text-slate-800") : "text-slate-400 italic font-normal"}`}>{s?.name || "tanpa nama"}</span>
        <PtBadge name={s?.company} colors={colors} />
      </div>
      <div className="flex items-center gap-1">
        <span className="font-mono text-[10px] text-slate-500">{port}.{slot}</span>
        {s && (isCur || s.status === "active") && <span data-testid={`slot-status-${port}-${slot}`} title={st.label} className={`h-2 w-2 rounded-full ${st.dot}`} />}
      </div>
    </button>
  );
}

export function PortGrid({ dev, slots, low, q, colors, onOpen }) {
  const map = useMemo(() => Object.fromEntries(slots.map((s) => [`${s.port}.${s.slot}`, s])), [slots]);
  const ports = Array.from({ length: dev.max_ports }, (_, i) => i + 1);
  const cols = Math.min(dev.max_slots, 8);
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="slot-grid">
      <div className="px-3 py-2 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
        Klik slot untuk memberi nama, PT, cek pulsa, atau mengaktifkan SIM · titik hijau = ON (Register OK)
      </div>
      {ports.map((p) => {
        const cur = currentOf(slots, p);
        const [txt, color] = cur ? SHORT[cur.status] || ["OFF", "text-slate-400"] : [(dev.locked_ports || []).includes(p) ? "LOCK" : "OFF", "text-slate-400"];
        const op = OPERATORS[cur?.operator];
        return (
          <div key={p} data-testid={`port-row-${p}`} className="grid grid-cols-[48px_1fr] items-center gap-2 px-3 py-2 border-b border-slate-100">
            <div data-testid={`port-state-${p}`} className="leading-tight">
              <div className="font-mono font-bold text-slate-800">{p}</div>
              <div className={`text-[9px] font-mono font-semibold ${color}`}>{txt}</div>
              {cur && op && <div className={`text-[9px] font-mono ${op.color}`}>{op.short}</div>}
            </div>
            <div className="grid gap-1 grid-cols-3 sm:grid-cols-4 lg:[grid-template-columns:var(--cols)]" style={{ "--cols": `repeat(${cols}, minmax(0, 1fr))` }}>
              {Array.from({ length: dev.max_slots }, (_, j) => j + 1).map((s) => (
                <Cell key={s} port={p} slot={s} s={map[`${p}.${s}`]} isCur={cur?.slot === s} colors={colors} q={q} onOpen={onOpen} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
